// HistoryGraphNew.jsx
import React, { useMemo, useEffect, useState, useRef } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import NewButton from "../Buttons/NewButton";
import { useMapToggles } from "../Map/ToggleContext";
import { icons } from "../../helpers/constants";

const CHART_HEIGHT = 150;

function findNearestIndexByX(data, x) {
	if (!data?.length) return -1;
	if (x <= data[0].x) return 0;
	if (x >= data[data.length - 1].x) return data.length - 1;

	let lo = 0,
		hi = data.length - 1;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		const xm = data[mid].x;
		if (xm === x) return mid;
		if (xm < x) lo = mid + 1;
		else hi = mid - 1;
	}
	const i1 = Math.max(0, Math.min(hi, data.length - 1));
	const i2 = Math.max(0, Math.min(lo, data.length - 1));
	return Math.abs(data[i1].x - x) <= Math.abs(data[i2].x - x) ? i1 : i2;
}

export default function HistoryGraphNew({ data, types, initialPoints = 25, unit = "km/h" }) {
	const { t } = useTranslation();
	const chartId = "chart2";
	const [buddy, setBuddy] = useState(null);
	const [buddyTime, setBuddyTime] = useState(null);
	const { routePoint, setRoutePoint } = useMapToggles();
	const [localRoutePoint, setLocalRoutePoint] = useState(null);
	const [progress, setProgress] = useState(0);
	const [statTypes, setStatTypes] = useState([]);
	const [playbackSpeed, setPlaybackSpeed] = useState(1);
	const [isPlaying, setIsPlaying] = useState(false); // ⬅ NEW
	const playTimerRef = useRef(null); // ⬅ NEW
	const playIdxRef = useRef(0); // ⬅ NEW
	
	// Map real data -> use index for x (uniform spacing), keep timestamp separately
	const s1 = useMemo(() => {
		if (Array.isArray(data) && data.length) {
			return data.map((item, idx) => ({
				x: idx, // <— uniform spacing
				y: item.info?.speed ?? 0,
				__id: item.id ?? item.info?.id ?? item.pointId ?? idx,
				__idx: idx,
				__ts: new Date(item.time).getTime(), // keep for formatting/tooltip if needed
			}));
		}
		return [];
	}, [data]);

	// Initial window range is in index units now
	const halfRange = useMemo(() => Math.max(1, initialPoints - 1) / 2, [initialPoints]);

	// Extend domain in index units
	const domainSeries = useMemo(() => {
		if (!s1.length) return [];
		const left = s1[0].x - halfRange; // can be negative
		const right = s1[s1.length - 1].x + halfRange; // can exceed last index
		return [
			{ x: left, y: 0 },
			{ x: right, y: 0 },
		];
	}, [s1, halfRange]);

	const { yMin, yMax } = useMemo(() => {
		if (!s1.length) return { yMin: 0, yMax: 1 };
		return { yMin: 0, yMax: Math.max(...s1.map((p) => p.y), 1) };
	}, [s1]);

	const lineHeightPx = useMemo(() => {
		if (buddy == null || yMax <= yMin) return 0;
		const ratio = (buddy - yMin) / (yMax - yMin);
		return Math.max(0, Math.min(CHART_HEIGHT, Math.round(ratio * CHART_HEIGHT)));
	}, [buddy, yMin, yMax]);


	const placeCenterDot = (min, max) => {
		if (!s1.length || !window?.ApexCharts) return;
		const cx = (min + max) / 2; // index-space
		const nearestIdx = findNearestIndexByX(s1, cx);
		if (nearestIdx >= 0) {
			const nearest = s1[nearestIdx];
			setBuddy(nearest.y);
			setBuddyTime(nearest.__ts); // 👈 save timestamp
			setRoutePoint(nearest.__idx); // or nearest.__id
			setLocalRoutePoint(nearest.__idx);
			const lastIdx = Math.max(1, s1.length - 1); // avoid /0
			const pct = Math.min(1, Math.max(0, nearest.__idx / lastIdx));
			setProgress(pct);
		}
	};

	useEffect(() => {
		const array = Object.entries(types).map(([key, value]) => ({
			key,
			label: value.name,
			unit: value.unit,
			show: value.show,
		}));
		setStatTypes(array);
	}, [types]);

	useEffect(() => {
		if (!s1.length || typeof window === "undefined" || !window.ApexCharts) return;
		if (routePoint == null || routePoint === localRoutePoint) return;

		// center the chart around the new routePoint
		const targetX = routePoint;
		const min = targetX - halfRange;
		const max = targetX + halfRange;

		// zoom to that range
		window.ApexCharts.exec(chartId, "zoomX", min, max);

		// update center/buddy/progress etc.
		placeCenterDot(min, max);

		// sync the local copy to avoid loops
		setLocalRoutePoint(routePoint);
	}, [routePoint, s1, halfRange]);

	// Initial zoom: center first index (0)
	useEffect(() => {
		if (!s1.length || typeof window === "undefined" || !window.ApexCharts) return;
		const first = s1[0].x; // 0
		const min = first - halfRange + 1;
		const max = first + halfRange - 0.5;
		const t = setTimeout(() => {
			window.ApexCharts.exec(chartId, "zoomX", min, max);
			placeCenterDot(min, max);
		}, 0);
		return () => clearTimeout(t);
	}, [s1, halfRange]);

	const series = useMemo(
		() => [
			{ name: "Speed", data: s1 },
			{ name: "_domain", data: domainSeries },
		],
		[s1, domainSeries]
	);

	const optionsMain = useMemo(
		() => ({
			chart: {
				id: chartId,
				// type: "line",
				// height: CHART_HEIGHT,
				zoom: { enabled: true, type: "x", autoScaleYaxis: false },
				toolbar: {
					show: false,
					autoSelected: "pan",
					tools: { pan: true, selection: false, zoom: false, zoomin: false, zoomout: false, reset: false },
				},
				animations: {
					enabled: true,
					easing: "easeinout",
					speed: 500,
					animateGradually: {
						enabled: false,
						delay: 150,
					},
					dynamicAnimation: {
						enabled: false,
						speed: 350,
					},
				},
				parentHeightOffset: 0,
				offsetX: 0,
				offsetY: 0,
				events: {
					scrolled: (ctx, { xaxis }) => {
						if (xaxis?.min != null && xaxis?.max != null) placeCenterDot(xaxis.min, xaxis.max);
						setIsPlaying(false);
					},
					zoomed: (ctx, { xaxis }) => {
						if (xaxis?.min != null && xaxis?.max != null) placeCenterDot(xaxis.min, xaxis.max);
					},
				},
			},
			annotations: { points: [] },
			colors: ["#000", "rgba(0,0,0,0)"],
			dataLabels: { enabled: false },
			stroke: { width: [2, 0], curve: "smooth", lineCap: "round" },
			fill: { opacity: [1, 0] },
			markers: { size: [0, 0] },
			legend: { show: false },
			tooltip: { enabled: false }, // if you enable later, format from point.__ts
			grid: {
				show: true,
				padding: { top: -28, right: 0, bottom: -11, left: 0 },
				yaxis: { lines: { show: false } },
			},
			noData: {
				text: t("statsistics.no_data"),
				align: "center",
				verticalAlign: "middle",
				style: { fontSize: "24px" },
			},
			xaxis: {
				show: false,
				type: "numeric", // <— numeric index (uniform)
				tooltip: { enabled: false },
				axisBorder: { show: false },
				axisTicks: { show: false },
				labels: { show: false },
			},
			yaxis: {
				show: false,
				type: "numeric",
				min: 0,
				max: yMax,
				tickAmount: 0,
				tooltip: { enabled: false },
				labels: { show: false },
			},
		}),
		[t, yMin, yMax]
	);

	const getDate = () => {
		if (!buddyTime) return "";
		return new Date(buddyTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
	};

	// ⬇️ Helper: jump to a specific index, keep everything in sync
	const jumpToIndex = (idx) => {
		if (!s1.length || typeof window === "undefined" || !window.ApexCharts) return;
		const clamped = Math.max(0, Math.min(idx, s1.length - 1));
		const min = clamped - halfRange;
		const max = clamped + halfRange;
		window.ApexCharts.exec(chartId, "zoomX", min, max);
		placeCenterDot(min, max);
		setLocalRoutePoint(clamped);
		setRoutePoint?.(clamped);
	};

	const runPlayback = () => {
		if (!s1.length) return;

		const atEnd = localRoutePoint >= s1.length - 1;

		// if we're stopped and at end, reset before playing again
		setIsPlaying((prev) => {
			if (!prev && atEnd) {
				playIdxRef.current = 0;
				jumpToIndex(0);
			}
			return !prev;
		});
	};

	// ⬇️ Rebuild the timer whenever play/pause or speed changes
	useEffect(() => {
		// clear any previous timer
		if (playTimerRef.current) {
			clearInterval(playTimerRef.current);
			playTimerRef.current = null;
		}
		if (!isPlaying || !s1.length) return;

		// start index = current point if available, else 0
		const startIdx = localRoutePoint != null ? localRoutePoint : 0;
		playIdxRef.current = startIdx;

		const stepEveryMs = Math.max(50, Math.floor(1000 / playbackSpeed)); // 1s per point @1x, 0.2s @5x, 0.1s @10x

		playTimerRef.current = setInterval(() => {
			// advance one point
			const next = playIdxRef.current + 1;
			if (next >= s1.length) {
				// reached the end: stop
				clearInterval(playTimerRef.current);
				playTimerRef.current = null;
				setIsPlaying(false);
				return;
			}
			playIdxRef.current = next;
			jumpToIndex(next);
		}, stepEveryMs);

		// cleanup
		return () => {
			if (playTimerRef.current) {
				clearInterval(playTimerRef.current);
				playTimerRef.current = null;
			}
		};
	}, [isPlaying, playbackSpeed, s1.length]); // rebuild on toggle/speed/data

	useEffect(() => {
		if (!s1.length && isPlaying) setIsPlaying(false);
	}, [s1.length, isPlaying]);

	const changePlaybackSpeed = () => {
		setPlaybackSpeed((prev) => {
			const next = prev === 10 ? 1 : prev === 1 ? 5 : 10;
			return next;
		});
	};

	const LINE_EXTRA = 30;
	const BASELINE = 24;

	const lineScale = (lineHeightPx + LINE_EXTRA) / (CHART_HEIGHT + LINE_EXTRA);
	const markerTranslate = lineHeightPx + BASELINE;
	const TRANSITION_MS = 250;
	return (
		<>
			<div className="position-relative">
				<div className="position-relative  cursor-pointer">
					<Chart options={optionsMain} series={series} type="area" height={CHART_HEIGHT} />
					<div className="bg-highlight position-absolute bottom-0" style={{ height: "4px", width: `${progress * 100}%` }} />
					<div className="position-absolute bottom-0 start-0 mb-2 ms-3 full-rounded bg-grey-blue white py-1 px-2">{getDate()}</div>
				</div>

				<div
					className="history-graph-line"
					style={{
						"--chart-h": `${CHART_HEIGHT}px`,
						"--line-scale": `${Math.max(0, Math.min(2, lineScale))}`
					}}
				/>

				<div
					className="history-graph-marker bg-gradient"
					style={{
						"--marker-translate": `${Math.max(0, markerTranslate)}px`,
					}}
				/>
				<div className="d-flex mt-3 mx-3">
					<NewButton
						notext={true}
						className="full-rounded bg-grey-blue size36 me-2"
						iconClass={"white-icon size_20"}
						icon={isPlaying ? icons.pause : icons.play}
						onClick={runPlayback}
					/>
					<NewButton className="full-rounded bg-grey-blue size36 white" onClick={changePlaybackSpeed}>
						{playbackSpeed}x
					</NewButton>

					<div className="horizontal-center full-rounded bg-gradient white-white py-1 px-2">
							{Math.round(buddy ?? 0)} {types?.speed?.unit ?? unit}
					</div>

					{/* <div className="select-wrap ms-auto w-25">
						<select value={"groupSelected"} onChange={(e) => null} className="bg-white form-control text-truncate rounded24">
							{statTypes.map((group, index) => (
								<option key={index} value={group.value}>
									{group.label}
								</option>
							))}
						</select>
					</div> */}
				</div>
			</div>
		</>
	);
}
